// Single source of truth for "which bids occupy a slot in the list right now"
// and "which of those have finished their skeleton phase". BidsList uses
// these to render; useVisibleBidsCount uses them for the badge. Anything
// downstream of the same `(bids, now)` pair sees identical results — by
// construction the counter cannot drift from what the user sees on screen.
//
// Reveal_at itself is server-stamped (`BidResource::REVEAL_RANGES_S`);
// SKELETON_MS is the post-reveal placeholder window the frontend layers on
// top before swapping skeleton → real card.

export const SKELETON_MS = 5000;

function revealTs(bid) {
    if (!bid?.reveal_at) return null;
    const ts = new Date(bid.reveal_at).getTime();
    return Number.isFinite(ts) ? ts : null;
}

// Bids past their reveal_at. Each one occupies a slot in the list — either
// as a skeleton (during SKELETON_MS) or a fully populated card after.
// Bids with no/invalid reveal_at (e.g. a fresh single-bid response from
// POST /bids) are treated as visible immediately.
// Sorted by id ASC so the drip order matches creation order.
export function visibleBids(bids, now) {
    return bids
        .filter(bid => {
            const ts = revealTs(bid);
            return ts === null || ts <= now;
        })
        .slice()
        .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

// True while a visible bid is still in its skeleton window
// (`reveal_at <= now < reveal_at + SKELETON_MS`). Bids without reveal_at
// skip the skeleton entirely.
export function isInSkeletonPhase(bid, now) {
    const ts = revealTs(bid);
    if (ts === null) return false;
    return now < ts + SKELETON_MS;
}

// Bids that have moved past the skeleton phase — i.e. real cards with
// writer info on display. This is the count the customer-facing badge
// ("X Bids") shows: it should equal the number of populated writer cards
// the user sees on screen.
export function revealedBids(bids, now) {
    return visibleBids(bids, now).filter(bid => !isInSkeletonPhase(bid, now));
}
